"use client";

import {
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useTranslations, useLocale } from "next-intl";
import { usePathname, useRouter } from "next/navigation";
import { PublicHeader } from "@/_components/layout/PublicHeader";
import { NextLink } from "@/_components/ui/NextLink";
import { FiChevronDown } from "react-icons/fi";
import {
  HiOutlineCollection,
  HiOutlineShieldCheck,
  HiOutlineTable,
  HiOutlineOfficeBuilding,
  HiOutlineQuestionMarkCircle,
} from "react-icons/hi";
import { BsRocket, BsStars, BsLightning, BsBuilding } from "react-icons/bs";
import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { IconType } from "react-icons";
import { PRICING_IDS } from "../_constants/pricing.ids";

const FAQ_KEYS = [
  "canCancel",
  "hasTrial",
  "paymentMethods",
  "freeExpiry",
  "upgradeAnytime",
  "teamPlan",
] as const;

const PLAN_KEYS = ["free", "pro", "ultimate", "enterprise"] as const;

const PLAN_ICONS: Record<(typeof PLAN_KEYS)[number], IconType> = {
  free: BsRocket,
  pro: BsStars,
  ultimate: BsLightning,
  enterprise: BsBuilding,
};

interface NavChild {
  icon: IconType;
  label: string;
  description: string;
  href: string;
  isLink?: boolean;
}

interface NavItem {
  id: string;
  label: string;
  href: string;
  sectionIds: string[];
  children?: NavChild[];
  isShowSubNav?: boolean;
}

function usePricingHash() {
  const [hash, setHash] = useState("");

  useEffect(() => {
    const sync = () => setHash(window.location.hash.replace("#", ""));
    sync();
    window.addEventListener("hashchange", sync);
    return () => window.removeEventListener("hashchange", sync);
  }, []);

  return hash;
}

function isNavItemActive(
  pathname: string,
  hash: string,
  sectionIds: string[],
): boolean {
  if (!pathname.includes("/pricing")) return false;
  const current = hash || PRICING_IDS.sections.plans;
  return sectionIds.some(
    (id) => current === id || current.startsWith(`${id}-`),
  );
}

export function PricingHeader() {
  const t = useTranslations("pricing");
  const locale = useLocale();
  const pathname = usePathname();
  const hash = usePricingHash();

  const pricingBase = `/${locale}/pricing`;

  const navItems: NavItem[] = useMemo(
    () => [
      {
        id: "plans",
        label: t("header.nav.plans"),
        href: `${pricingBase}#${PRICING_IDS.sections.plans}`,
        sectionIds: [PRICING_IDS.sections.plans, PRICING_IDS.sections.trust],
        children: [
          {
            icon: HiOutlineCollection,
            label: t("header.subnav.planCards.title"),
            description: t("header.subnav.planCards.description"),
            href: `${pricingBase}#${PRICING_IDS.sections.plans}`,
            isLink: true,
          },
          {
            icon: HiOutlineShieldCheck,
            label: t("header.subnav.trust.title"),
            description: t("header.subnav.trust.description"),
            href: `${pricingBase}#${PRICING_IDS.sections.trust}`,
            isLink: true,
          },
          ...PLAN_KEYS.map((planKey) => ({
            icon: PLAN_ICONS[planKey],
            label: t(`plans.${planKey}.name`),
            description: t(`plans.${planKey}.description`),
            href: `${pricingBase}#${PRICING_IDS.sections.plans}`,
            isLink: true,
          })),
        ],
        isShowSubNav: false,
      },
      {
        id: "compare",
        label: t("header.nav.compare"),
        href: `${pricingBase}#${PRICING_IDS.sections.compare}`,
        sectionIds: [PRICING_IDS.sections.compare],
        children: [
          {
            icon: HiOutlineTable,
            label: t("header.subnav.featureTable.title"),
            description: t("header.subnav.featureTable.description"),
            href: `${pricingBase}#${PRICING_IDS.sections.compare}`,
            isLink: true,
          },
        ],
        isShowSubNav: false,
      },
      {
        id: "enterprise",
        label: t("header.nav.enterprise"),
        href: `${pricingBase}#${PRICING_IDS.sections.enterprise}`,
        sectionIds: [PRICING_IDS.sections.enterprise],
        children: [
          {
            icon: HiOutlineOfficeBuilding,
            label: t("header.subnav.enterpriseOverview.title"),
            description: t("header.subnav.enterpriseOverview.description"),
            href: `${pricingBase}#${PRICING_IDS.sections.enterprise}`,
            isLink: true,
          },
          {
            icon: HiOutlineOfficeBuilding,
            label: t("enterprise.bookDemo"),
            description: t("header.subnav.viewSection"),
            href: `${pricingBase}#${PRICING_IDS.sections.enterprise}`,
            isLink: true,
          },
          {
            icon: HiOutlineOfficeBuilding,
            label: t("enterprise.contactSales"),
            description: t("header.subnav.viewSection"),
            href: `${pricingBase}#${PRICING_IDS.sections.enterprise}`,
            isLink: true,
          },
        ],
        isShowSubNav: false,
      },
      {
        id: "faq",
        label: t("header.nav.faq"),
        href: `${pricingBase}#${PRICING_IDS.sections.faq}`,
        sectionIds: [PRICING_IDS.sections.faq],
        children: FAQ_KEYS.map((key) => ({
          icon: HiOutlineQuestionMarkCircle,
          label: t(`faq.${key}.question`),
          description: t("header.subnav.viewSection"),
          href: `${pricingBase}#${PRICING_IDS.sections.faqItem(key)}`,
          isLink: true,
        })),
        isShowSubNav: false,
      },
    ],
    [t, pricingBase],
  );

  const desktopNav = (
    <HStack id={PRICING_IDS.header.desktopNav} gap={1}>
      {navItems.map((item) => (
        <NavDropdown
          key={item.id}
          item={item}
          isActive={isNavItemActive(pathname, hash, item.sectionIds)}
        />
      ))}
    </HStack>
  );

  const mobileNav = (onClose: () => void) =>
    navItems.map((item) => (
      <MobileNavAccordion
        key={item.id}
        item={item}
        isActive={isNavItemActive(pathname, hash, item.sectionIds)}
        onClose={onClose}
      />
    ));

  return (
    <Box id={PRICING_IDS.header.root}>
      <Box
        as="style"
        dangerouslySetInnerHTML={{
          __html:
            "@media (prefers-reduced-motion: no-preference) { html { scroll-behavior: smooth; } }",
        }}
      />
      <PublicHeader
        screenName={t("header.title")}
        navbar={desktopNav}
        mobileNavbar={mobileNav}
        showBookDemo={false}
      />
    </Box>
  );
}

function NavDropdown({ item, isActive }: { item: NavItem; isActive: boolean }) {
  const [open, setOpen] = useState(false);
  const hoverTimeout = useRef<NodeJS.Timeout | null>(null);
  const router = useRouter();

  const showSubNav = item.isShowSubNav !== false;

  const handleMouseEnter = useCallback(() => {
    if (!showSubNav) return;
    if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
    setOpen(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (!showSubNav) return;
    hoverTimeout.current = setTimeout(() => setOpen(false), 150);
  }, []);

  const handleChildClick = (child: NavChild) => {
    if (!child.href) return;
    setOpen(false);
    router.push(child.href);
  };

  return (
    <Box
      id={PRICING_IDS.header.desktopItem(item.id)}
      position="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Button
        id={PRICING_IDS.header.desktopItemButton(item.id)}
        visual="none-style"
        size="sm"
        textStyle="13px"
        fontWeight={isActive ? "600" : "400"}
        color={isActive ? "primary.600" : "neutral.500"}
        bg={isActive ? "primary.50" : "transparent"}
        borderRadius="full"
        px={4}
        _hover={{ color: "primary.600", bg: "primary.50" }}
        onClick={() => router.push(item.href)}
      >
        <HStack gap={1}>
          <Text>{item.label}</Text>
          {showSubNav && (
            <Box
              display="inline-flex"
              transition="transform 0.25s cubic-bezier(.4,1.5,.5,1)"
              transform={open ? "rotate(180deg)" : "rotate(0deg)"}
            >
              <Icon as={FiChevronDown} boxSize={3.5} />
            </Box>
          )}
        </HStack>
      </Button>

      {showSubNav && (
        <Box
          id={PRICING_IDS.header.desktopDropdown(item.id)}
          position="absolute"
          top="100%"
          left="50%"
          transform="translateX(-50%)"
          pt={2}
          opacity={open ? 1 : 0}
          visibility={open ? "visible" : "hidden"}
          transition="all 0.2s ease"
          zIndex={1400}
          pointerEvents={open ? "auto" : "none"}
        >
          <Box
            bg="white"
            borderRadius="14px"
            boxShadow="0 12px 40px rgba(0,0,0,0.1), 0 2px 10px rgba(0,0,0,0.06)"
            border="1px solid"
            borderColor="blackAlpha.100"
            minW="280px"
            overflow="hidden"
            p={2}
          >
            <VStack
              id={PRICING_IDS.header.desktopDropdownList(item.id)}
              align="stretch"
              gap={0.5}
            >
              {item.children?.map((child, index) => (
                <SubNavItemCard
                  key={`${child.href}-${child.label}`}
                  child={child}
                  isLink={child.isLink}
                  debugId={PRICING_IDS.header.desktopSubitem(
                    item.id,
                    index + 1,
                  )}
                  linkId={PRICING_IDS.header.desktopSubitemLink(
                    item.id,
                    index + 1,
                  )}
                  boxId={PRICING_IDS.header.desktopSubitemBox(
                    item.id,
                    index + 1,
                  )}
                  onClick={() => handleChildClick(child)}
                />
              ))}
            </VStack>
          </Box>
        </Box>
      )}
    </Box>
  );
}

function SubNavItemCard({
  child,
  isLink,
  onClick,
  debugId,
  linkId,
  boxId,
}: {
  child: NavChild;
  isLink?: boolean;
  onClick: () => void;
  debugId: string;
  linkId: string;
  boxId: string;
}) {
  const content = (
    <HStack
      id={debugId}
      gap={3}
      p={2.5}
      borderRadius="10px"
      transition="all 0.15s ease"
      _hover={{ bg: "neutral.50" }}
      align="start"
      cursor="pointer"
    >
      <Flex
        w="34px"
        h="34px"
        borderRadius="8px"
        bg="primary.50"
        align="center"
        justify="center"
        flexShrink={0}
        mt={0.5}
      >
        <Icon as={child.icon} boxSize={4} color="primary.500" />
      </Flex>
      <Box>
        <Text
          textStyle="13px"
          fontWeight="500"
          color="neutral.800"
          lineHeight="1.3"
          mb={0.5}
        >
          {child.label}
        </Text>
        <Text textStyle="11px" color="neutral.400" lineHeight="1.4">
          {child.description}
        </Text>
      </Box>
    </HStack>
  );

  if (isLink) {
    return (
      <NextLink
        id={linkId}
        href={child.href}
        _hover={{ textDecoration: "none" }}
        onClick={(e) => {
          e.preventDefault();
          onClick();
        }}
      >
        {content}
      </NextLink>
    );
  }

  return (
    <Box id={boxId} onClick={onClick}>
      {content}
    </Box>
  );
}

function MobileNavAccordion({
  item,
  isActive,
  onClose,
}: {
  item: NavItem;
  isActive: boolean;
  onClose: () => void;
}) {
  const [expanded, setExpanded] = useState(isActive);
  const router = useRouter();

  const showSubNav = item.isShowSubNav !== false;

  const handleChildClick = (child: NavChild) => {
    onClose();
    router.push(child.href);
  };

  return (
    <Box id={PRICING_IDS.header.mobileItem(item.id)} w="full">
      <HStack
        id={PRICING_IDS.header.mobileItemTrigger(item.id)}
        gap={1}
        cursor="pointer"
        onClick={() => {
          if (!isActive || !showSubNav) {
            router.push(item.href);
            return;
          }
          setExpanded((prev) => !prev);
        }}
        py={1}
      >
        <Text
          fontWeight={isActive ? "medium" : "light"}
          fontFamily="heading"
          textStyle="xl"
          color={isActive ? "primary.600" : "neutral.700"}
        >
          {item.label}
        </Text>
        {showSubNav && (
          <Box
            display="inline-flex"
            transition="transform 0.25s cubic-bezier(.4,1.5,.5,1)"
            transform={expanded ? "rotate(180deg)" : "rotate(0deg)"}
          >
            <Icon as={FiChevronDown} boxSize={4} color="neutral.500" />
          </Box>
        )}
      </HStack>

      {showSubNav && (
        <Box
          id={PRICING_IDS.header.mobileItemPanel(item.id)}
          overflow="hidden"
          maxH={expanded ? "600px" : "0"}
          opacity={expanded ? 1 : 0}
          transition="all 0.3s ease"
        >
          <VStack
            id={PRICING_IDS.header.mobileItemList(item.id)}
            align="stretch"
            gap={1}
            pl={2}
            pt={2}
            pb={3}
          >
            {item.children?.map((child, index) => (
              <HStack
                id={PRICING_IDS.header.mobileSubitem(item.id, index + 1)}
                key={`${child.href}-${child.label}`}
                gap={3}
                p={2}
                borderRadius="8px"
                _hover={{ bg: "neutral.50" }}
                cursor="pointer"
                onClick={() => handleChildClick(child)}
              >
                <Flex
                  w="30px"
                  h="30px"
                  borderRadius="6px"
                  bg="primary.50"
                  align="center"
                  justify="center"
                  flexShrink={0}
                >
                  <Icon as={child.icon} boxSize={3.5} color="primary.500" />
                </Flex>
                <Box>
                  <Text textStyle="sm" fontWeight="500" color="neutral.700">
                    {child.label}
                  </Text>
                  <Text textStyle="xs" color="neutral.400">
                    {child.description}
                  </Text>
                </Box>
              </HStack>
            ))}
          </VStack>
        </Box>
      )}
    </Box>
  );
}
